import { Link, useParams, Navigate, useNavigate } from 'react-router-dom';
import Footer from '@/pages/home/components/Footer';
import { getArticleBySlug } from '@/mocks/articles';

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const article = slug ? getArticleBySlug(slug) : undefined;

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  if (!article) {
    return <Navigate to="/" replace />;
  }

  const handleBackToHome = () => {
    navigate('/');
    setTimeout(() => window.scrollTo(0, 0), 100);
  };

  return (
    <main>
      {/* Article Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-background-50/95 backdrop-blur-md shadow-sm">
        <div className="w-full px-4 md:px-8 lg:px-10">
          <div className="flex items-center justify-between h-14 md:h-16">
            <Link to="/" onClick={() => window.scrollTo(0, 0)} className="flex items-center gap-2 whitespace-nowrap cursor-pointer flex-shrink-0">
              <div className="w-7 h-7 md:w-8 md:h-8 rounded-lg bg-primary-500 flex items-center justify-center">
                <i className="ri-file-list-3-line text-white text-sm md:text-base"></i>
              </div>
              <span className="font-heading text-sm md:text-lg font-semibold text-foreground-900">
                cssbuyspreadsheet<span className="text-accent-500">.team</span>
              </span>
            </Link>
            <button
              onClick={handleBackToHome}
              className="inline-flex items-center gap-1.5 px-3 md:px-4 py-2 text-sm font-medium rounded-full border border-primary-500 text-primary-600 hover:bg-primary-50 transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-arrow-left-line"></i>
              Voltar ao Início
            </button>
          </div>
        </div>
      </nav>

      {/* Article Hero */}
      <section className="relative w-full min-h-[380px] md:min-h-[460px] flex items-center overflow-hidden pt-14 md:pt-16">
        <div className="absolute inset-0">
          <img
            src="https://readdy.ai/api/search-image?query=Clean%20organized%20workspace%20with%20laptop%20displaying%20spreadsheet%20and%20product%20images%2C%20bright%20natural%20light%2C%20minimal%20desk%20setup%2C%20warm%20neutral%20aesthetic%2C%20editorial%20photography%2C%20productive%20atmosphere%2C%20cream%20and%20beige%20palette&width=1600&height=700&seq=art-hero-universal&orientation=landscape"
            alt={article.title}
            title={article.title}
            className="w-full h-full object-cover object-top"
          />
        </div>
        <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-black/15 to-black/30"></div>
        <div className="relative w-full px-6 md:px-10 lg:px-16">
          <div className="max-w-3xl">
            <span className="inline-block px-4 py-1.5 rounded-full border border-white/40 text-white/90 text-xs md:text-sm font-medium mb-6 whitespace-nowrap">
              {article.tag}
            </span>
            <h1 className="font-heading text-2xl md:text-4xl lg:text-5xl font-semibold text-white leading-tight mb-4">
              {article.title}
            </h1>
            <p className="text-white/70 text-sm md:text-base">
              Publicado em {article.date} · Tempo de leitura: {article.readTime}
            </p>
          </div>
        </div>
      </section>

      {/* Article Body */}
      <article className="relative w-full py-16 md:py-20 bg-secondary-50">
        <div className="w-full px-6 md:px-10 lg:px-16">
          <div className="flex flex-col lg:flex-row gap-12 lg:gap-20 max-w-5xl mx-auto">
            <div className="lg:w-1/4">
              <div className="sticky top-24">
                <span className="text-xs font-medium text-foreground-500 uppercase tracking-wider">
                  / cssbuy spreadsheet
                </span>
                <div className="mt-6 space-y-2">
                  <p className="text-xs text-foreground-500 font-semibold uppercase tracking-wider mb-3">Índice</p>
                  {article.sections.map((section, idx) => (
                    <a
                      key={section.id}
                      href={`#${section.id}`}
                      className="block text-sm text-foreground-600 hover:text-primary-500 transition-colors cursor-pointer"
                    >
                      {idx + 1}. {section.heading.replace(/^\d+\.\s*/, '')}
                    </a>
                  ))}
                </div>
              </div>
            </div>

            <div className="lg:w-3/4">
              <div className="prose-custom space-y-8 text-foreground-800 text-sm md:text-base leading-relaxed">
                {article.sections.map((section) => (
                  <section key={section.id} id={section.id} className={section.id !== article.sections[0].id ? 'pt-4' : ''}>
                    <h2 className="font-heading text-2xl md:text-3xl font-semibold text-foreground-900 mb-5">
                      {section.heading}
                    </h2>
                    {section.paragraphs.map((para, pIdx) => (
                      <p key={pIdx} className="mb-4">
                        {para}
                      </p>
                    ))}
                  </section>
                ))}

                {/* Internal Links */}
                <div className="flex items-center gap-5 pt-10 mt-10 border-t border-secondary-300/50">
                  <div className="flex -space-x-3">
                    {[1, 2, 3, 4].map((i) => (
                      <div
                        key={i}
                        className="w-10 h-10 md:w-11 md:h-11 rounded-full bg-secondary-400 border-2 border-secondary-50 flex items-center justify-center overflow-hidden"
                      >
                        <i className="ri-user-line text-secondary-700 text-sm"></i>
                      </div>
                    ))}
                  </div>
                  <div>
                    <p className="text-sm font-medium text-foreground-800">Equipe CSSBuySpreadsheet</p>
                    <p className="text-xs text-foreground-500">Especialistas em planilhas CSSBuy desde 2020</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </article>

      {/* Article CTA */}
      <section className="relative w-full flex flex-col md:flex-row min-h-[380px] md:h-[420px]">
        <div className="md:w-1/2 relative overflow-hidden">
          <img
            src="https://readdy.ai/api/search-image?query=Fashion%20items%20and%20spreadsheet%20concept%20on%20cream%20desk%2C%20soft%20natural%20daylight%2C%20editorial%20photography%2C%20warm%20neutral%20aesthetic%2C%20organized%20shopping%20theme%2C%20clean%20composition%2C%20inviting%20lifestyle%20look%2C%20gentle%20shadows&width=800&height=600&seq=art-cta-v2-left&orientation=landscape"
            alt="Comece a economizar agora"
            title="CSSBuySpreadsheet - Comece Agora"
            className="w-full h-full object-cover object-top"
          />
        </div>
        <div className="md:w-1/2 bg-background-50 flex items-center justify-center px-6 md:px-12 py-14 md:py-0">
          <div className="max-w-md text-center">
            <h2 className="font-heading text-2xl md:text-3xl font-semibold text-foreground-900 mb-5">
              Pronto para Começar a Economizar?
            </h2>
            <p className="text-foreground-600 text-sm md:text-base leading-relaxed mb-8">
              Acesse agora a planilha CSSBuy mais completa do mercado e descubra como é fácil comprar produtos de qualidade com os melhores preços.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className="inline-flex items-center justify-center gap-2 px-7 py-3.5 rounded-full bg-primary-500 text-background-50 font-semibold text-sm md:text-base hover:bg-primary-600 transition-all cursor-pointer whitespace-nowrap"
              >
                Buy It Now
                <i className="ri-arrow-right-line text-lg"></i>
              </a>
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className="inline-flex items-center justify-center gap-2 px-7 py-3.5 rounded-full border border-primary-500 text-primary-600 font-semibold text-sm md:text-base hover:bg-primary-50 transition-all cursor-pointer whitespace-nowrap"
              >
                Ver Planilha Completa
              </a>
            </div>
            <div className="mt-8 pt-6 border-t border-background-200/70">
              <button onClick={handleBackToHome} className="text-sm text-primary-500 hover:text-primary-600 transition-colors cursor-pointer">
                ← Voltar para a página inicial do CSSBuySpreadsheet
              </button>
            </div>
          </div>
        </div>
      </section>

      <Footer />

      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify({
        '@context': 'https://schema.org',
        '@type': 'Article',
        headline: article.title,
        description: article.metaDescription,
        image: article.imageUrl,
        author: {
          '@type': 'Organization',
          name: 'CSSBuySpreadsheet'
        },
        datePublished: '2026-06-20',
        dateModified: '2026-06-27',
        publisher: {
          '@type': 'Organization',
          name: 'CSSBuySpreadsheet',
          url: 'https://cssbuyspreadsheet.team/',
          logo: {
            '@type': 'ImageObject',
            url: 'https://static.readdy.ai/image/2df560051ef0cafd8dabf8a049058ba4/d367cf6745ee47f3fc82c76d3a54c50c.png'
          }
        },
        mainEntityOfPage: {
          '@type': 'WebPage',
          '@id': `https://cssbuyspreadsheet.team/artigo/${article.slug}`
        },
        inLanguage: 'pt-BR'
      }) }} />
    </main>
  );
}